import React from 'react'
import { Card, Link, Icon, FlexBox, Typography, AlertBox, tokens } from '@vp/swan'

const isEmptyOrWhitespace = (text: string): boolean => {
  const cleaned = text
    .replace(/&nbsp;/gi, '')
    .replace(/\s+/g, '')
    .replace(/\n/g, '')
    .trim()
  return cleaned === ''
}

const cleanBrTags = (html: string): string => {
  const replacements: Array<[RegExp, string]> = [
    [/(<img[^>]*>)\s*(?:&nbsp;)?\s*(?:<br\s*\/?>)+/gi, '$1'],
    [/(?:<br\s*\/?>\s*)+(<\/[^>]+>)/gi, '$1'],
    [/(?:<br\s*\/?>\s*){2,}/gi, ''],
    [/&nbsp;\s*<br\s*\/?>/gi, ''],
    [/<br\s*\/?>\s*<\/p>/gi, '</p>'],
  ]

  return replacements.reduce((cleaned, [pattern, replacement]) => cleaned.replace(pattern, replacement), html)
}

const shouldRemoveElement = (element: Element): boolean => {
  const tagName = element.tagName.toLowerCase()
  const textContent = element.textContent || ''
  const hasMediaContent = element.querySelector('iframe, img, video, audio, embed, object')
  if (hasMediaContent) {
    return false
  }

  if (['ol', 'ul'].includes(tagName)) {
    return false
  }

  if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
    return isEmptyOrWhitespace(textContent)
  }

  if (tagName === 'p') {
    return isEmptyOrWhitespace(textContent)
  }

  return false
}

export const parseArticleBody = (htmlContent: string): React.ReactElement[] => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(htmlContent, 'text/html')
  const elements: React.ReactElement[] = []
  let htmlBuffer = ''
  let keyCounter = 0

  const isTableWithSingleCell = (element: Element): boolean => {
    if (element.tagName.toLowerCase() !== 'table') return false

    const rows = element.querySelectorAll('tr')
    if (rows.length !== 1) return false

    const cells = rows[0].querySelectorAll('td')
    return cells.length === 1
  }

  const isCrossLinkTable = (element: Element): boolean => {
    if (!isTableWithSingleCell(element)) return false

    const bgColor = element.getAttribute('style')?.toLowerCase()
    const hasCorrectColor = bgColor?.includes('#e5f5fd') || bgColor?.includes('rgb(229, 245, 253)')

    // Check if it has a link
    const link = element.querySelector('a')

    return Boolean(hasCorrectColor && link)
  }

  const isInfoHighlightTable = (element: Element): boolean => {
    if (!isTableWithSingleCell(element)) return false

    const bgColor = element.getAttribute('style')?.toLowerCase()
    const hasCorrectColor = bgColor?.includes('#d3d3d3') || bgColor?.includes('rgb(211, 211, 211)')

    // Should NOT have a link (to distinguish from cross-link)
    const link = element.querySelector('a')

    return Boolean(hasCorrectColor && !link)
  }

  const extractLinkData = (table: Element): { href: string; text: string } | null => {
    const link = table.querySelector('a')
    if (!link) return null

    return {
      href: link.getAttribute('href') || '#',
      text: link.textContent || '',
    }
  }

  const extractTextData = (table: Element): string => {
    const cell = table.querySelector('td')
    return cell?.textContent || ''
  }

  const flushHtmlBuffer = () => {
    if (htmlBuffer.trim()) {
      const cleanedHtml = cleanBrTags(htmlBuffer)
      const tempDoc = new DOMParser().parseFromString(cleanedHtml, 'text/html')
      const children = Array.from(tempDoc.body.children)
      const childElements: React.ReactElement[] = []

      const HEADING_TAGS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6']
      const LIST_TAGS = ['ol', 'ul']

      const isHeading = (tagName: string) => HEADING_TAGS.includes(tagName)
      const isList = (tagName: string) => LIST_TAGS.includes(tagName)

      const processElement = (index: number): number => {
        const child = children[index]

        if (shouldRemoveElement(child)) return 1

        const tagName = child.tagName.toLowerCase()
        const nextChild = children[index + 1]
        const nextTagName = nextChild?.tagName.toLowerCase()

        // Case 1: Heading with following content
        if (isHeading(tagName) && nextChild && !shouldRemoveElement(nextChild)) {
          if (!isHeading(nextTagName)) {
            const nextNextChild = children[index + 2]
            const isParagraph = nextTagName === 'p'
            const nextNextIsList = nextNextChild && isList(nextNextChild.tagName.toLowerCase()) && !shouldRemoveElement(nextNextChild)

            if (isParagraph && nextNextIsList) {
              childElements.push(
                <FlexBox key={`heading-para-list-group-${keyCounter++}`} flexDirection="column" gap="3" className="article-heading-group">
                  <FlexBox flexDirection="column" className="article-heading">
                    <div dangerouslySetInnerHTML={{ __html: child.outerHTML }} />
                  </FlexBox>
                  <FlexBox flexDirection="column" className="article-element">
                    <div dangerouslySetInnerHTML={{ __html: nextChild.outerHTML }} />
                  </FlexBox>
                  <FlexBox flexDirection="column" className="article-list">
                    <div dangerouslySetInnerHTML={{ __html: nextNextChild.outerHTML }} />
                  </FlexBox>
                </FlexBox>,
              )
              return 3
            }

            const nextClassName = isList(nextTagName) ? 'article-list' : 'article-element'
            childElements.push(
              <FlexBox key={`heading-content-group-${keyCounter++}`} flexDirection="column" gap="3" className="article-heading-group">
                <FlexBox flexDirection="column" className="article-heading">
                  <div dangerouslySetInnerHTML={{ __html: child.outerHTML }} />
                </FlexBox>
                <FlexBox flexDirection="column" className={nextClassName}>
                  <div dangerouslySetInnerHTML={{ __html: nextChild.outerHTML }} />
                </FlexBox>
              </FlexBox>,
            )
            return 2
          }

          // Case 1c: Consecutive headings
          childElements.push(
            <FlexBox key={`element-${keyCounter++}`} flexDirection="column" className="article-heading">
              <div dangerouslySetInnerHTML={{ __html: child.outerHTML }} />
            </FlexBox>,
          )
          return 1
        }

        if (!isHeading(tagName)) {
          const isParagraph = tagName === 'p'
          const nextIsList = nextChild && isList(nextTagName) && !shouldRemoveElement(nextChild)

          if (isParagraph && nextIsList) {
            childElements.push(
              <FlexBox key={`para-list-group-${keyCounter++}`} flexDirection="column" gap="3" className="article-para-list-group">
                <FlexBox flexDirection="column" className="article-element">
                  <div dangerouslySetInnerHTML={{ __html: child.outerHTML }} />
                </FlexBox>
                <FlexBox flexDirection="column" className="article-list">
                  <div dangerouslySetInnerHTML={{ __html: nextChild.outerHTML }} />
                </FlexBox>
              </FlexBox>,
            )
            return 2
          }

          const className = isList(tagName) ? 'article-list' : 'article-element'
          childElements.push(
            <FlexBox key={`element-${keyCounter++}`} flexDirection="column" className={className}>
              <div dangerouslySetInnerHTML={{ __html: child.outerHTML }} />
            </FlexBox>,
          )
          return 1
        }
        childElements.push(
          <FlexBox key={`element-${keyCounter++}`} flexDirection="column" className="article-heading">
            <div dangerouslySetInnerHTML={{ __html: child.outerHTML }} />
          </FlexBox>,
        )
        return 1
      }

      children.reduce((currentIndex, _, index) => {
        if (currentIndex > index) return currentIndex
        const skip = processElement(currentIndex)
        return currentIndex + skip
      }, 0)

      if (childElements.length > 0) {
        elements.push(
          <FlexBox key={`wrapper-${keyCounter++}`} flexDirection="column" className="article-content-flex">
            {childElements}
          </FlexBox>,
        )
      }
      htmlBuffer = ''
    }
  }

  Array.from(doc.body.childNodes).forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element

      if (isCrossLinkTable(element)) {
        flushHtmlBuffer()

        const linkData = extractLinkData(element)
        if (linkData) {
          elements.push(
            <FlexBox key={`wrapper-${keyCounter++}`} flexDirection="column" className="article-content-flex">
              <Card
                key={`card-${keyCounter++}`}
                skin="link"
                style={{ backgroundColor: tokens.SwanSemColorBgAccent, border: `1px solid ${tokens.SwanSemColorBorderAccent}` }}
              >
                <FlexBox gap="4" alignItems="center" justifyContent="space-between" style={{ width: '100%' }}>
                  <FlexBox gap="4" alignItems="center" style={{ flex: 1 }}>
                    <Icon mt={0} iconType="wayfinding" size="standard" skin="standard" />
                    <Typography fontSkin="body-standard-bold">{linkData.text}</Typography>
                  </FlexBox>
                  <Icon mt={0} iconType="arrowRight" size="standard" skin="standard" />
                  <Link covering href={linkData.href} target="_blank" rel="noopener noreferrer" />
                </FlexBox>
              </Card>
            </FlexBox>,
          )
        }
      } else if (isInfoHighlightTable(element)) {
        flushHtmlBuffer()

        const text = extractTextData(element)
        if (text) {
          elements.push(
            <FlexBox key={`wrapper-${keyCounter++}`} flexDirection="column" className="article-content-flex">
              <AlertBox key={`alert-${keyCounter++}`} skin="info">
                {text}
              </AlertBox>
            </FlexBox>,
          )
        }
      } else {
        htmlBuffer += element.outerHTML
      }
    } else {
      const textNode = node as Text
      htmlBuffer += textNode.textContent || ''
    }
  })

  flushHtmlBuffer()

  return elements
}
